import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import useStyles from './styles';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, TextField, Skeleton, Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { Icon, InputWithIcon, ExpandedTooltip } from '@livable/saas-react-components';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { PortfolioActionsMenu } from '../PortfolioActionsMenu';
import { PortfolioNavFilterMenu } from './PortfoliosNavFilterMenu';
import { PropertiesNavFilterMenu } from './PropertiesNavFilterMenu';
import { CreateEditPortfolioModal } from 'src/components/shared/CreateEditPortfolioModal';
import { PropertyActionsMenu } from 'src/components/shared/PropertyActionsMenu';
import { Link as RouterLink, useMatch, useNavigate, useParams } from 'react-router-dom';
import { PROPERTY_STATUS_ENUM } from 'src/constants/property';
import { parseJwt } from 'src/core/services/jwt';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { useDebounce } from 'src/utils/hooks/useDebounce';
import { useVirtualizer } from '@tanstack/react-virtual';
export const SmartNav = observer(_ref => {
  var _store$homeDashboardS, _store$homeDashboardS2, _store$homeDashboardS3, _store$homeDashboardS4, _store$homeDashboardS5, _store$homeDashboardS6, _store$homeDashboardS8, _store$homeDashboardS9, _store$homeDashboardS0, _store$homeDashboardS10, _store$homeDashboardS11, _store$homeDashboardS12, _store$homeDashboardS13, _store$homeDashboardS14, _store$homeDashboardS15, _store$homeDashboardS16;
  let name = _ref.name,
    _ref$isIcon = _ref.isIcon,
    isIcon = _ref$isIcon === void 0 ? false : _ref$isIcon,
    _ref$isFilter = _ref.isFilter,
    isFilter = _ref$isFilter === void 0 ? true : _ref$isFilter,
    icon = _ref.icon,
    isLoading = _ref.isLoading,
    subtitle = _ref.subtitle,
    _ref$isFullPage = _ref.isFullPage,
    isFullPage = _ref$isFullPage === void 0 ? false : _ref$isFullPage,
    _ref$isPortfoliosNav = _ref.isPortfoliosNav,
    isPortfoliosNav = _ref$isPortfoliosNav === void 0 ? false : _ref$isPortfoliosNav,
    _ref$isPropertiesNav = _ref.isPropertiesNav,
    isPropertiesNav = _ref$isPropertiesNav === void 0 ? false : _ref$isPropertiesNav,
    _ref$selectAllLabel = _ref.selectAllLabel,
    selectAllLabel = _ref$selectAllLabel === void 0 ? undefined : _ref$selectAllLabel,
    listItems = _ref.listItems,
    selectActionCallback = _ref.selectActionCallback,
    handleShowEnrollModal = _ref.handleShowEnrollModal,
    triggerDeleteModalOpen = _ref.triggerDeleteModalOpen,
    setDeleteModalProps = _ref.setDeleteModalProps,
    handleShowManageAccessModal = _ref.handleShowManageAccessModal,
    handleShowCreateEditPortfolioTrigger = _ref.handleShowCreateEditPortfolioTrigger,
    triggerRefreshPortfolioData = _ref.triggerRefreshPortfolioData,
    openPropertyDetails = _ref.openPropertyDetails,
    handleTabToBillingChange = _ref.handleTabToBillingChange,
    handleRemovePortfolioFromProperty = _ref.handleRemovePortfolioFromProperty,
    handleOpenAssignPortfoliosToPropertyModal = _ref.handleOpenAssignPortfoliosToPropertyModal;
  const classes = useStyles();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const store = useStore();
  const navigate = useNavigate();
  const smartNavScrollRef = useRef(null);
  const smartNavBodyListRef = useRef(null);
  const _useParams = useParams(),
    navPortfolioId = _useParams.navPortfolioId,
    navPropertyId = _useParams.navPropertyId;
  const defaultNBMUrl = '/active_dashboard/:navCompanyId/:navPortfolioId/:navPropertyId';
  const isBilling = useMatch("".concat(defaultNBMUrl, "/billing"));
  const isResidents = useMatch("".concat(defaultNBMUrl, "/residents"));
  const isDetails = useMatch("".concat(defaultNBMUrl, "/details"));
  const isAppfolio = useMatch("".concat(defaultNBMUrl, "/appfolio-batches"));
  const accessTokenAsync = parseJwt(localStorage.getItem(AUTH_TOKEN_KEYS.accessToken));
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchKey = _useState2[0],
    setSearchKey = _useState2[1];
  const debouncedSearchKey = useDebounce(searchKey, 300);
  const handleSearchKeyChange = e => {
    setSearchKey(e.target.value);
  };
  const filteredListItems = useMemo(() => {
    if (!listItems) return [];
    const searchPhrase = new RegExp(debouncedSearchKey, 'gi');
    if (isPortfoliosNav) {
      return listItems.filter(i => {
        var _i$properties;
        if (!searchKey) return true;
        const matchPortfolio = " ".concat(i.name).search(searchPhrase) > 0;
        const matchProperty = (_i$properties = i.properties) === null || _i$properties === void 0 ? void 0 : _i$properties.some(p => " ".concat(p.dashboard.propertyAddress).search(searchPhrase) > 0);
        return matchPortfolio || matchProperty;
      });
    }
    if (isPropertiesNav) {
      return listItems.filter(i => !searchKey ? true : " ".concat(i.dashboard.propertyAddress).search(searchPhrase) > 0);
    }
    return listItems;
  }, [listItems, searchKey, debouncedSearchKey, isPortfoliosNav, isPropertiesNav]);
  const NavItemSkeleton = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItem
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.smartNavItemIcon, " icon")
    }, /*#__PURE__*/React.createElement(Skeleton, {
      variant: "rectangular",
      animation: "wave",
      height: 20,
      width: 20
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetails
    }, /*#__PURE__*/React.createElement("b", null, /*#__PURE__*/React.createElement(Skeleton, {
      variant: "text",
      animation: "wave",
      height: 24
    }))));
  };
  const getActiveRow = id => {
    return isPortfoliosNav && store.homeDashboardStore.selectedPortfolioId === id || isPropertiesNav && store.homeDashboardStore.selectedPropertyId === id;
  };
  let delayTimer;
  const GetNavItem = observer(_ref2 => {
    var _store$properties$del, _store$allocations$ge, _store$portfolios$del, _store$properties$del2, _store$allocations$ge2, _store$portfolios$del2, _payload$dashboard, _payload$dashboard2, _payload$propertyPort, _payload$dashboard3;
    let payload = _ref2.payload;
    return /*#__PURE__*/React.createElement(React.Fragment, null, (((_store$properties$del = store.properties.deletePropertyAsync) === null || _store$properties$del === void 0 || (_store$properties$del = _store$properties$del.inProgressUnicIds) === null || _store$properties$del === void 0 ? void 0 : _store$properties$del.includes(payload.id)) || ((_store$allocations$ge = store.allocations.getCurrentCycleAsync) === null || _store$allocations$ge === void 0 || (_store$allocations$ge = _store$allocations$ge.inProgressUnicIds) === null || _store$allocations$ge === void 0 ? void 0 : _store$allocations$ge.includes(payload.id)) || ((_store$portfolios$del = store.portfolios.deletePortfolioAsync) === null || _store$portfolios$del === void 0 || (_store$portfolios$del = _store$portfolios$del.inProgressUnicIds) === null || _store$portfolios$del === void 0 ? void 0 : _store$portfolios$del.includes(payload.id))) && /*#__PURE__*/React.createElement(Skeleton, {
      variant: "text",
      animation: "wave",
      height: 54,
      width: '100%'
    }), !((_store$properties$del2 = store.properties.deletePropertyAsync) !== null && _store$properties$del2 !== void 0 && (_store$properties$del2 = _store$properties$del2.inProgressUnicIds) !== null && _store$properties$del2 !== void 0 && _store$properties$del2.includes(payload.id) || (_store$allocations$ge2 = store.allocations.getCurrentCycleAsync) !== null && _store$allocations$ge2 !== void 0 && (_store$allocations$ge2 = _store$allocations$ge2.inProgressUnicIds) !== null && _store$allocations$ge2 !== void 0 && _store$allocations$ge2.includes(payload.id) || (_store$portfolios$del2 = store.portfolios.deletePortfolioAsync) !== null && _store$portfolios$del2 !== void 0 && (_store$portfolios$del2 = _store$portfolios$del2.inProgressUnicIds) !== null && _store$portfolios$del2 !== void 0 && _store$portfolios$del2.includes(payload.id)) && /*#__PURE__*/React.createElement("div", {
      id: payload.id,
      key: payload.id,
      className: "".concat(classes.smartNavItem, " ").concat(getActiveRow(payload.id) && 'active'),
      onMouseEnter: () => {
        if (isPropertiesNav) {
          delayTimer = setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            selectActionCallback && selectActionCallback({
              entityType: isPropertiesNav ? 'property' : 'portfolio',
              _id: payload.id,
              prefetchDataOnly: true
            });
          }, 500);
        }
      },
      onMouseLeave: () => {
        if (isPropertiesNav) {
          clearTimeout(delayTimer);
        }
      },
      onClick: () => {
        if (isPropertiesNav && (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive) {
          // navigate to property setup
          navigate("/new_property?not_completed_id=".concat(payload.id));
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          selectActionCallback && selectActionCallback({
            entityType: isPropertiesNav ? 'property' : 'portfolio',
            _id: payload.id,
            prefetchDataOnly: false
          });
        }
      }
    }, isIcon && /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.smartNavItemIcon, " ").concat(icon ? 'icon' : '')
    }, isPortfoliosNav && /*#__PURE__*/React.createElement(Icon, {
      state: getActiveRow(payload.id) ? undefined : 'lightGrey',
      color: getActiveRow(payload.id) ? 'grey' : undefined,
      type: icon
    }), isPropertiesNav && /*#__PURE__*/React.createElement(Icon, {
      type: icon,
      color: getActiveRow(payload.id) ? 'grey' : undefined,
      state: (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active && 'success' || (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive && 'info' || undefined
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetails
    }, isPortfoliosNav && /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: payload.name,
      placement: 'top',
      type: "grey"
    }, /*#__PURE__*/React.createElement("b", null, payload.name)), isPropertiesNav && /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: (_payload$dashboard = payload.dashboard) === null || _payload$dashboard === void 0 ? void 0 : _payload$dashboard.propertyAddress,
      placement: 'top',
      type: (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active && 'success-bold' || (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive && 'info'
    }, /*#__PURE__*/React.createElement("b", null, (_payload$dashboard2 = payload.dashboard) === null || _payload$dashboard2 === void 0 ? void 0 : _payload$dashboard2.propertyAddress)), subtitle && /*#__PURE__*/React.createElement("span", null, subtitle)), !isPropertiesNav && !isPortfoliosNav && /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemNumber
    }, "18"), isPortfoliosNav && /*#__PURE__*/React.createElement(PortfolioActionsMenu, {
      portfolioId: payload.id,
      selectedPortfolio: payload,
      menuTriggerExternal: /*#__PURE__*/React.createElement("span", {
        onMouseEnter: event => {
          event.stopPropagation();
          event.preventDefault();
          clearTimeout(delayTimer);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "dotdotdot"
      })),
      triggerDeleteModalOpen: triggerDeleteModalOpen,
      setDeleteModalProps: setDeleteModalProps,
      triggerRenameModalOpen: val => {
        handleShowCreateEditPortfolioTrigger(val, payload);
      },
      handleShowManageAccessModal: handleShowManageAccessModal
    }), isPropertiesNav && /*#__PURE__*/React.createElement(PropertyActionsMenu, {
      id: payload.id,
      portfolioId: (_payload$propertyPort = payload.propertyPortfolioInfo) === null || _payload$propertyPort === void 0 ? void 0 : _payload$propertyPort.id,
      isActive: (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active,
      handleShowEnrollModal: () => {
        var _payload$propertyPort2;
        handleShowEnrollModal(true, {
          portfolioId: (_payload$propertyPort2 = payload.propertyPortfolioInfo) === null || _payload$propertyPort2 === void 0 ? void 0 : _payload$propertyPort2.id,
          propertyId: payload.id,
          enablePortfolioSelect: false,
          enablePropertySelect: false
        });
      },
      name: (_payload$dashboard3 = payload.dashboard) === null || _payload$dashboard3 === void 0 ? void 0 : _payload$dashboard3.propertyAddress,
      selectActionCallback: selectActionCallback,
      openPropertyDetails: openPropertyDetails,
      menuTriggerExternal: /*#__PURE__*/React.createElement("span", {
        onMouseEnter: event => {
          event.stopPropagation();
          event.preventDefault();
          clearTimeout(delayTimer);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "dotdotdot"
      }))
    })));
  });
  const GetPropertyItem = observer(_ref3 => {
    var _store$properties$del3, _store$allocations$ge3, _store$portfolios$del3, _store$properties$del4, _store$allocations$ge4, _store$portfolios$del4, _payload$propertyPort3, _payload$dashboard4, _payload$dashboard5, _payload$propertyPort7, _payload$propertyPort9, _payload$dashboard6;
    let payload = _ref3.payload;
    return /*#__PURE__*/React.createElement(React.Fragment, null, (((_store$properties$del3 = store.properties.deletePropertyAsync) === null || _store$properties$del3 === void 0 || (_store$properties$del3 = _store$properties$del3.inProgressUnicIds) === null || _store$properties$del3 === void 0 ? void 0 : _store$properties$del3.includes(payload.id)) || ((_store$allocations$ge3 = store.allocations.getCurrentCycleAsync) === null || _store$allocations$ge3 === void 0 || (_store$allocations$ge3 = _store$allocations$ge3.inProgressUnicIds) === null || _store$allocations$ge3 === void 0 ? void 0 : _store$allocations$ge3.includes(payload.id)) || ((_store$portfolios$del3 = store.portfolios.deletePortfolioAsync) === null || _store$portfolios$del3 === void 0 || (_store$portfolios$del3 = _store$portfolios$del3.inProgressUnicIds) === null || _store$portfolios$del3 === void 0 ? void 0 : _store$portfolios$del3.includes(payload.id))) && /*#__PURE__*/React.createElement(Skeleton, {
      variant: "text",
      animation: "wave",
      height: 54,
      width: '100%'
    }), !((_store$properties$del4 = store.properties.deletePropertyAsync) !== null && _store$properties$del4 !== void 0 && (_store$properties$del4 = _store$properties$del4.inProgressUnicIds) !== null && _store$properties$del4 !== void 0 && _store$properties$del4.includes(payload.id) || (_store$allocations$ge4 = store.allocations.getCurrentCycleAsync) !== null && _store$allocations$ge4 !== void 0 && (_store$allocations$ge4 = _store$allocations$ge4.inProgressUnicIds) !== null && _store$allocations$ge4 !== void 0 && _store$allocations$ge4.includes(payload.id) || (_store$portfolios$del4 = store.portfolios.deletePortfolioAsync) !== null && _store$portfolios$del4 !== void 0 && (_store$portfolios$del4 = _store$portfolios$del4.inProgressUnicIds) !== null && _store$portfolios$del4 !== void 0 && _store$portfolios$del4.includes(payload.id)) && /*#__PURE__*/React.createElement("div", {
      id: payload.id,
      key: payload.id,
      className: "".concat(classes.smartNavItem, " ").concat(store.homeDashboardStore.selectedPropertyId === payload.id && store.homeDashboardStore.selectedPortfolioId === ((_payload$propertyPort3 = payload.propertyPortfolioInfo) === null || _payload$propertyPort3 === void 0 ? void 0 : _payload$propertyPort3.id) && 'active'),
      onClick: () => {
        if (selectActionCallback) {
          var _payload$propertyPort4;
          if ((payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive) {
            store.addNewProperty.resetStoreToInitValues();
            store.vendors.resetStore();
            navigate("/active_dashboard?not_completed_id=".concat(payload.id));
          }
          selectActionCallback({
            entityType: 'portfolio',
            _id: (_payload$propertyPort4 = payload.propertyPortfolioInfo) === null || _payload$propertyPort4 === void 0 ? void 0 : _payload$propertyPort4.id,
            prefetchDataOnly: false
          });
          selectActionCallback({
            entityType: 'property',
            _id: payload.id,
            prefetchDataOnly: false
          });
          if ((payload === null || payload === void 0 ? void 0 : payload.propertyStatus) !== PROPERTY_STATUS_ENUM.Inactive) {
            if (isBilling || isResidents || isDetails || isAppfolio) {
              var _payload$propertyPort5;
              navigate("/active_dashboard/".concat(accessTokenAsync['custom:companyid'], "/").concat((_payload$propertyPort5 = payload.propertyPortfolioInfo) === null || _payload$propertyPort5 === void 0 ? void 0 : _payload$propertyPort5.id, "/").concat(payload.id, "/").concat(isBilling ? 'billing' : isResidents ? 'residents' : isDetails ? 'details' : 'appfolio-batches'));
            } else {
              var _payload$propertyPort6;
              navigate("/active_dashboard/".concat(accessTokenAsync['custom:companyid'], "/").concat((_payload$propertyPort6 = payload.propertyPortfolioInfo) === null || _payload$propertyPort6 === void 0 ? void 0 : _payload$propertyPort6.id, "/").concat(payload.id, "/billing"));
            }
          }
        }
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.smartNavItemIcon, " ").concat(icon ? 'icon' : '', " ").concat((payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active ? 'success' : (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive ? 'info' : '')
    }, /*#__PURE__*/React.createElement(Icon, {
      type: 'building',
      state: "lightGrey"
    }), (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Inactive && /*#__PURE__*/React.createElement("i", null, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    }))), /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetails
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: (_payload$dashboard4 = payload.dashboard) === null || _payload$dashboard4 === void 0 ? void 0 : _payload$dashboard4.propertyAddress,
      placement: 'top',
      type: (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active && 'success-bold-dark'
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, (_payload$dashboard5 = payload.dashboard) === null || _payload$dashboard5 === void 0 ? void 0 : _payload$dashboard5.propertyAddress))), /*#__PURE__*/React.createElement(PropertyActionsMenu, {
      id: payload.id,
      portfolioId: (_payload$propertyPort7 = payload.propertyPortfolioInfo) === null || _payload$propertyPort7 === void 0 ? void 0 : _payload$propertyPort7.id,
      selectActionCallback: selectActionCallback,
      isActive: (payload === null || payload === void 0 ? void 0 : payload.propertyStatus) === PROPERTY_STATUS_ENUM.Active,
      handleShowEnrollModal: () => {
        var _payload$propertyPort8;
        handleShowEnrollModal(true, {
          portfolioId: (_payload$propertyPort8 = payload.propertyPortfolioInfo) === null || _payload$propertyPort8 === void 0 ? void 0 : _payload$propertyPort8.id,
          propertyId: payload.id,
          enablePortfolioSelect: false,
          enablePropertySelect: false
        });
      },
      isInDefaultPortfolio: (_payload$propertyPort9 = payload.propertyPortfolioInfo) === null || _payload$propertyPort9 === void 0 ? void 0 : _payload$propertyPort9.isDefault,
      name: (_payload$dashboard6 = payload.dashboard) === null || _payload$dashboard6 === void 0 ? void 0 : _payload$dashboard6.propertyAddress,
      openPropertyDetails: openPropertyDetails,
      menuTriggerExternal: /*#__PURE__*/React.createElement("span", {
        onMouseEnter: event => {
          event.stopPropagation();
          event.preventDefault();
          clearTimeout(delayTimer);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "dotdotdot"
      })),
      changeActiveProperty: () => {
        if (selectActionCallback) {
          var _payload$propertyPort0;
          selectActionCallback({
            entityType: 'portfolio',
            _id: (_payload$propertyPort0 = payload.propertyPortfolioInfo) === null || _payload$propertyPort0 === void 0 ? void 0 : _payload$propertyPort0.id,
            prefetchDataOnly: false
          });
          selectActionCallback({
            entityType: 'property',
            _id: payload.id,
            prefetchDataOnly: false
          });
        }
        if (handleTabToBillingChange) handleTabToBillingChange();
      },
      handleRemovePortfolioFromProperty: () => {
        var _payload$propertyPort1;
        return handleRemovePortfolioFromProperty({
          payload,
          portfolioId: (_payload$propertyPort1 = payload.propertyPortfolioInfo) === null || _payload$propertyPort1 === void 0 ? void 0 : _payload$propertyPort1.id
        });
      },
      handleOpenAssignPortfoliosToPropertyModal: () => {
        handleOpenAssignPortfoliosToPropertyModal({
          property: payload
        });
      }
    })));
  });
  const VirtualizedPropertyList = observer(function VirtualizedPropertyList(_ref4) {
    let items = _ref4.items;
    const listContainerRef = useRef(null);
    // Each expanded portfolio has its own virtualizer but they SHARE one scroll
    // container. `scrollMargin` tells this virtualizer where its own block starts
    // inside that container; without it the shared scrollTop maps to the wrong
    // (end-of-list) indices, so a lower list paints only its first + last rows.
    const _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      scrollMargin = _useState4[0],
      setScrollMargin = _useState4[1];
    useLayoutEffect(() => {
      const scrollEl = smartNavScrollRef.current;
      const listEl = listContainerRef.current;
      const contentEl = smartNavBodyListRef.current;
      if (!scrollEl || !listEl) return undefined;
      const recompute = () => {
        const margin = listEl.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top + scrollEl.scrollTop;
        setScrollMargin(prev => Math.abs(prev - margin) > 0.5 ? margin : prev);
      };
      recompute();
      const resizeObserver = new ResizeObserver(recompute);
      if (contentEl) resizeObserver.observe(contentEl);
      return () => resizeObserver.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length]);
    const rowVirtualizer = useVirtualizer({
      count: items.length,
      getScrollElement: () => smartNavScrollRef.current,
      estimateSize: () => 54,
      overscan: 5,
      scrollMargin,
      // Fixed 54px rows (no measureElement): rows are single-line (nowrap +
      // ellipsis), so heights are deterministic. Dynamic measurement would make
      // several virtualizers fight over the shared scroll position.
      scrollToFn: (offset, opts) => {
        var _smartNavScrollRef$cu, _opts$behavior;
        if ((opts === null || opts === void 0 ? void 0 : opts.reason) !== 'manual') return;
        (_smartNavScrollRef$cu = smartNavScrollRef.current) === null || _smartNavScrollRef$cu === void 0 || _smartNavScrollRef$cu.scrollTo({
          top: offset,
          behavior: (_opts$behavior = opts === null || opts === void 0 ? void 0 : opts.behavior) !== null && _opts$behavior !== void 0 ? _opts$behavior : 'auto'
        });
      }
    });
    if (!items.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      ref: listContainerRef,
      style: {
        position: 'relative',
        height: rowVirtualizer.getTotalSize(),
        width: '100%'
      }
    }, rowVirtualizer.getVirtualItems().map(virtualRow => {
      const property = items[virtualRow.index];
      return /*#__PURE__*/React.createElement("div", {
        key: virtualRow.key,
        "data-index": virtualRow.index,
        style: {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 54,
          transform: "translateY(".concat(virtualRow.start - scrollMargin, "px)")
        }
      }, /*#__PURE__*/React.createElement(GetPropertyItem, {
        payload: property
      }));
    }));
  });
  const GetNavItemCombined = observer(_ref5 => {
    var _store$properties$del5, _store$allocations$ge5, _store$portfolios$del5, _store$properties$del6, _store$allocations$ge6, _store$portfolios$del6, _payload$properties2;
    let payload = _ref5.payload;
    const filteredProperties = useMemo(() => {
      var _payload$properties;
      if (!((_payload$properties = payload.properties) !== null && _payload$properties !== void 0 && _payload$properties.length)) return [];
      const searchPhrase = new RegExp(debouncedSearchKey, 'gi');
      return payload.properties.filter(i => {
        return searchKey.length > 0 ? " ".concat(i.dashboard.propertyAddress).search(searchPhrase) > 0 : true;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payload.properties, debouncedSearchKey, searchKey]);
    return /*#__PURE__*/React.createElement(React.Fragment, null, (((_store$properties$del5 = store.properties.deletePropertyAsync) === null || _store$properties$del5 === void 0 || (_store$properties$del5 = _store$properties$del5.inProgressUnicIds) === null || _store$properties$del5 === void 0 ? void 0 : _store$properties$del5.includes(payload.id)) || ((_store$allocations$ge5 = store.allocations.getCurrentCycleAsync) === null || _store$allocations$ge5 === void 0 || (_store$allocations$ge5 = _store$allocations$ge5.inProgressUnicIds) === null || _store$allocations$ge5 === void 0 ? void 0 : _store$allocations$ge5.includes(payload.id)) || ((_store$portfolios$del5 = store.portfolios.deletePortfolioAsync) === null || _store$portfolios$del5 === void 0 || (_store$portfolios$del5 = _store$portfolios$del5.inProgressUnicIds) === null || _store$portfolios$del5 === void 0 ? void 0 : _store$portfolios$del5.includes(payload.id))) && /*#__PURE__*/React.createElement(Skeleton, {
      variant: "text",
      animation: "wave",
      height: 54,
      width: '100%'
    }), !((_store$properties$del6 = store.properties.deletePropertyAsync) !== null && _store$properties$del6 !== void 0 && (_store$properties$del6 = _store$properties$del6.inProgressUnicIds) !== null && _store$properties$del6 !== void 0 && _store$properties$del6.includes(payload.id) || (_store$allocations$ge6 = store.allocations.getCurrentCycleAsync) !== null && _store$allocations$ge6 !== void 0 && (_store$allocations$ge6 = _store$allocations$ge6.inProgressUnicIds) !== null && _store$allocations$ge6 !== void 0 && _store$allocations$ge6.includes(payload.id) || (_store$portfolios$del6 = store.portfolios.deletePortfolioAsync) !== null && _store$portfolios$del6 !== void 0 && (_store$portfolios$del6 = _store$portfolios$del6.inProgressUnicIds) !== null && _store$portfolios$del6 !== void 0 && _store$portfolios$del6.includes(payload.id)) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Accordion, {
      disableGutters: true,
      key: payload.id,
      expanded: payload.navExpanded,
      onChange: (event, expanded) => payload.setNavExpanded(expanded),
      className: classes.smartNavItemPortfolioAccordion
    }, /*#__PURE__*/React.createElement(AccordionSummary, {
      id: "portfolio-nav-item",
      expandIcon: /*#__PURE__*/React.createElement(Icon, {
        state: "grey",
        type: "arrow-line-right"
      })
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        width: '100%'
      }
    }, /*#__PURE__*/React.createElement("div", {
      id: payload.id,
      key: payload.id,
      className: "".concat(classes.smartNavItemPortfolio, " ").concat(store.homeDashboardStore.selectedPortfolioId === payload.id && 'active'),
      onClick: event => {
        event.stopPropagation();
        event.preventDefault();
        selectActionCallback({
          entityType: 'portfolio',
          _id: payload === null || payload === void 0 ? void 0 : payload.id,
          prefetchDataOnly: false
        });
        selectActionCallback({
          entityType: 'portfolio_details',
          _id: null,
          prefetchDataOnly: false
        });
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.smartNavItemIcon, " ").concat(icon ? 'icon' : '')
    }, /*#__PURE__*/React.createElement(Icon, {
      state: store.homeDashboardStore.selectedPortfolioId === payload.id ? undefined : 'lightGrey',
      color: store.homeDashboardStore.selectedPortfolioId === payload.id ? 'grey' : undefined,
      type: 'folder-house-variant'
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetails
    }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: payload.name,
      placement: 'top',
      type: "grey"
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "h6"
    }, payload.name))), /*#__PURE__*/React.createElement(PortfolioActionsMenu, {
      portfolioId: payload.id,
      selectedPortfolio: payload,
      menuTriggerExternal: /*#__PURE__*/React.createElement("span", {
        onMouseEnter: event => {
          event.stopPropagation();
          event.preventDefault();
          clearTimeout(delayTimer);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        type: "dotdotdot"
      })),
      triggerDeleteModalOpen: triggerDeleteModalOpen,
      setDeleteModalProps: setDeleteModalProps,
      triggerRenameModalOpen: val => {
        handleShowCreateEditPortfolioTrigger(val, payload);
      },
      handleShowManageAccessModal: handleShowManageAccessModal,
      selectActionCallback: selectActionCallback
    })))), /*#__PURE__*/React.createElement(AccordionDetails, null, !!((_payload$properties2 = payload.properties) !== null && _payload$properties2 !== void 0 && _payload$properties2.length) ? filteredProperties.length ? /*#__PURE__*/React.createElement(VirtualizedPropertyList, {
      items: filteredProperties
    }) : null : /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.smartNavItem, " empty")
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetails
    }, store.account.featureFlagsStore.getIsMultiAccountAccessEnabled && /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetailsEmpty
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetailsEmptyCaption
    }, "Portfolio is currently empty"), /*#__PURE__*/React.createElement("div", {
      className: classes.smartNavItemDetailsEmptyAction
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      size: "small",
      onClick: event => {
        event.stopPropagation();
        event.preventDefault();
        selectActionCallback({
          entityType: 'add_properties_to_portfolio',
          _id: payload.id,
          prefetchDataOnly: false
        });
      }
    }, t('portfolios_properties_page.add_property'))))))))));
  });
  useEffect(() => {
    setTimeout(() => {
      if (navPortfolioId) {
        if (!store.homeDashboardStore.getPortfolioById(navPortfolioId)) {
          navigate("/active_dashboard/".concat(accessTokenAsync['custom:companyid'], "/all/all"), {
            replace: true
          });
          return;
        }
        store.homeDashboardStore.selectActivePortfolioId(navPortfolioId);
        if (navPortfolioId !== 'all' && navPropertyId === 'all') {
          store.homeDashboardStore.selectActivePropertyId('portfolio_details');
        }
      }
      if (navPropertyId && navPropertyId !== 'all') {
        if (!store.homeDashboardStore.getPropertyById(navPropertyId)) {
          navigate("/active_dashboard/".concat(accessTokenAsync['custom:companyid'], "/all/all"), {
            replace: true
          });
          return;
        }
        store.homeDashboardStore.selectActivePropertyId(navPropertyId);
      }
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.smartNav, " ").concat(!store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen ? 'close' : '')
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavContent
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.smartNavControll, " ").concat(!store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen ? 'close' : ''),
    onClick: () => {
      store.homeDashboardStore.getCurrentCompany.changeCompanyPropertiesNavOpenState();
    },
    'custom-attribute': 'properties-smart-nav'
  }, /*#__PURE__*/React.createElement("span", null, name), /*#__PURE__*/React.createElement(ExpandedTooltip, {
    title: t("general.".concat(store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen ? 'collapse' : 'expand')) + ' [',
    placement: !store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen && (_store$homeDashboardS = store.homeDashboardStore) !== null && _store$homeDashboardS !== void 0 && _store$homeDashboardS.enableShortcutsHintState ? 'right' : 'top',
    type: "default",
    key: 'properties-smart-nav' + ((_store$homeDashboardS2 = store.homeDashboardStore) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.enableShortcutsHintState),
    open: ((_store$homeDashboardS3 = store.homeDashboardStore) === null || _store$homeDashboardS3 === void 0 ? void 0 : _store$homeDashboardS3.enableShortcutsHintState) || undefined
    // open={true}
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    type: "arrow-line-left"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavSetting
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavSettingSearch
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    variant: "outlined",
    size: "small",
    fullWidth: true,
    placeholder: t('general.search'),
    value: searchKey,
    onChange: handleSearchKeyChange
  }))), isFilter && /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavSettingFilter
  }, isPortfoliosNav && (!!(searchKey !== null && searchKey !== void 0 && searchKey.length) || !((_store$homeDashboardS4 = store.homeDashboardStore.getCurrentCompany) !== null && _store$homeDashboardS4 !== void 0 && _store$homeDashboardS4.portfolioNavOptions.isFilterDefault)) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    title: t('general.reset_filter'),
    placement: 'top',
    type: "warning",
    key: 'properties-smart-nav-portfolio-reset' + ((_store$homeDashboardS5 = store.homeDashboardStore) === null || _store$homeDashboardS5 === void 0 ? void 0 : _store$homeDashboardS5.enableShortcutsHintState),
    open: store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen && ((_store$homeDashboardS6 = store.homeDashboardStore) === null || _store$homeDashboardS6 === void 0 ? void 0 : _store$homeDashboardS6.enableShortcutsHintState) || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    size: "small",
    onClick: () => {
      var _store$homeDashboardS7;
      setSearchKey('');
      (_store$homeDashboardS7 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS7 === void 0 || _store$homeDashboardS7.portfolioNavOptions.resetFilterOptions();
    },
    'custom-attribute': '39-smart-nav'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "error"
  })))), isPropertiesNav && (!!(searchKey !== null && searchKey !== void 0 && searchKey.length) || !((_store$homeDashboardS8 = store.homeDashboardStore.getCurrentCompany) !== null && _store$homeDashboardS8 !== void 0 && _store$homeDashboardS8.propertyNavOptions.isFilterDefault)) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    title: t('general.reset_filter'),
    placement: 'top',
    type: "warning",
    key: 'properties-smart-nav-property-reset' + ((_store$homeDashboardS9 = store.homeDashboardStore) === null || _store$homeDashboardS9 === void 0 ? void 0 : _store$homeDashboardS9.enableShortcutsHintState),
    open: store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen && ((_store$homeDashboardS0 = store.homeDashboardStore) === null || _store$homeDashboardS0 === void 0 ? void 0 : _store$homeDashboardS0.enableShortcutsHintState) || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    size: "small",
    onClick: () => {
      var _store$homeDashboardS1;
      setSearchKey('');
      (_store$homeDashboardS1 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS1 === void 0 || _store$homeDashboardS1.propertyNavOptions.resetFilterOptions();
    },
    'custom-attribute': '40-smart-nav'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "error"
  })))), /*#__PURE__*/React.createElement("div", null, isPortfoliosNav && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PortfolioNavFilterMenu, {
    isNavOpen: store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen
    // listItems={listItems}
    ,
    listItems: store.homeDashboardStore.getCurrentCompanyPortfolios,
    menuTriggerExternal: /*#__PURE__*/React.createElement(Icon, {
      className: classes.smartNavSettingFilterIcon,
      type: "filter-new"
    })
  })), isPropertiesNav && /*#__PURE__*/React.createElement(PropertiesNavFilterMenu, {
    listItems: (_store$homeDashboardS10 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS10 === void 0 ? void 0 : _store$homeDashboardS10.propertyNavOptions.sortedItems,
    menuTriggerExternal: /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: t('general.reset_filter'),
      placement: (_store$homeDashboardS11 = store.homeDashboardStore) !== null && _store$homeDashboardS11 !== void 0 && _store$homeDashboardS11.enableShortcutsHintState ? 'left' : 'top',
      type: "warning",
      key: 'properties-smart-nav-property-reset-icon'
    }, /*#__PURE__*/React.createElement(Icon, {
      className: classes.smartNavSettingFilterIcon,
      type: "filter-new"
    }))
  })), /*#__PURE__*/React.createElement("div", null, isPortfoliosNav && store.account.featureFlagsStore.getIsMultiAccountAccessEnabled && /*#__PURE__*/React.createElement(CreateEditPortfolioModal, {
    triggerRefresh: triggerRefreshPortfolioData,
    customTrigger: /*#__PURE__*/React.createElement(ExpandedTooltip, {
      title: t('portfolios_properties_page.new_portfolio'),
      key: 'new_portfolio_btn' + ((_store$homeDashboardS12 = store.homeDashboardStore) === null || _store$homeDashboardS12 === void 0 ? void 0 : _store$homeDashboardS12.enableShortcutsHintState),
      placement: (_store$homeDashboardS13 = store.homeDashboardStore) !== null && _store$homeDashboardS13 !== void 0 && _store$homeDashboardS13.enableShortcutsHintState ? 'right' : 'top',
      type: "info",
      open: store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen && ((_store$homeDashboardS14 = store.homeDashboardStore) === null || _store$homeDashboardS14 === void 0 ? void 0 : _store$homeDashboardS14.enableShortcutsHintState) || undefined
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      size: "small",
      'custom-attribute': '37-smart-nav'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    })))
  }), isPropertiesNav && !store.company.companyFeatureStore.isAppfolio && /*#__PURE__*/React.createElement(ExpandedTooltip, {
    title: t('portfolios_properties_page.add_new_property'),
    key: 'add_new_property_btn' + ((_store$homeDashboardS15 = store.homeDashboardStore) === null || _store$homeDashboardS15 === void 0 ? void 0 : _store$homeDashboardS15.enableShortcutsHintState),
    placement: 'top',
    type: "info",
    open: store.homeDashboardStore.getCurrentCompany.companyPropertiesNavOpen && ((_store$homeDashboardS16 = store.homeDashboardStore) === null || _store$homeDashboardS16 === void 0 ? void 0 : _store$homeDashboardS16.enableShortcutsHintState) || undefined
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    size: "small",
    component: RouterLink,
    id: "addNewProperty",
    to: "/new_property",
    'custom-attribute': '38-smart-nav'
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "plus"
  })))))), /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavCollapseAll,
    onClick: () => {
      if (listItems.findIndex(i => i.navExpanded === true) !== -1) {
        listItems.map(el => el.setNavExpanded(false));
      } else {
        listItems.map(el => el.setNavExpanded(true));
      }
    }
  }, listItems.findIndex(i => i.navExpanded === true) !== -1 ? t('general.collapse_all') : t('general.expand_all')), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.smartNavBody, " ").concat(isFullPage ? 'isFullPage' : '', " ").concat(store.payment.failedPaymentMethods && store.payment.warningPaymentMethods && history.pathname !== '/admin' && history.pathname !== '/appfolio' && history.pathname !== '/information_banners' ? 'paymentMarginWarningBoth' : (store.payment.failedPaymentMethods || store.payment.warningPaymentMethods) && history.pathname !== '/admin' && history.pathname !== '/appfolio' && history.pathname !== '/information_banners' ? 'paymentMarginWarning' : ''),
    ref: smartNavScrollRef
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavBodyList,
    ref: smartNavBodyListRef
  }, !isLoading && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.smartNavItem, " ").concat(store.homeDashboardStore.selectedPropertyId === 'all' && store.homeDashboardStore.selectedPortfolioId === 'all' && 'active'),
    onClick: event => {
      event.stopPropagation();
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      selectActionCallback && selectActionCallback({
        entityType: isPropertiesNav ? 'property' : 'portfolio',
        _id: 'all',
        prefetchDataOnly: false
      });
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavItemDetails
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, selectAllLabel)), !isPropertiesNav && !isPortfoliosNav && /*#__PURE__*/React.createElement("div", {
    className: classes.smartNavItemNumber
  }, "8")), !isLoading && listItems && !!(listItems !== null && listItems !== void 0 && listItems.length) && filteredListItems.map(item => {
    if (isPortfoliosNav) {
      return /*#__PURE__*/React.createElement(GetNavItemCombined, {
        payload: item,
        key: item.id
      });
    } else {
      return /*#__PURE__*/React.createElement(GetNavItem, {
        payload: item,
        key: item.id
      });
    }
  }), isLoading && Array.from({
    length: 19
  }, (_, i) => i).map(i => /*#__PURE__*/React.createElement(NavItemSkeleton, {
    key: i
  }))))));
});